import type { FrontDeskSettings, SaveVickiGoalRequest, VickiGoal } from '@deskworks/shared-types';
import { useQuery } from '@tanstack/react-query';
import { CalendarCheck, Check, HeartPulse, Pencil, Plus, Target, Trash2, X } from 'lucide-react';
import { useState } from 'react';
import { RelativeTime } from '@/components/relative-time';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { api } from '@/lib/api';
import { useApiMutation } from '@/lib/hooks';
import { t } from '@/lib/i18n';
import { qk } from '@/lib/query-keys';

/** How often VICKI's heartbeat may wake her, in minutes; '' is off. */
const BEATS: { value: string; label: string }[] = [
  { value: '', label: t('Off') },
  { value: '15', label: t('every 15 minutes') },
  { value: '30', label: t('every 30 minutes') },
  { value: '60', label: t('every hour') },
  { value: '120', label: t('every 2 hours') },
  { value: '240', label: t('every 4 hours') },
  { value: '480', label: t('every 8 hours') },
  { value: '1440', label: t('once a day') },
];

/** The hour of the daily round is kept in UTC; people pick it in their own time. */
const offsetHours = Math.round(-new Date().getTimezoneOffset() / 60);
const toLocal = (utc: number) => (((utc + offsetHours) % 24) + 24) % 24;
const toUtc = (local: number) => (((local - offsetHours) % 24) + 24) % 24;

/**
 * VICKI's goals (docs/features/front-desk.md#goals): what the business wants to reach, set by its owners and admins.
 * VICKI writes where each one stands; changes she wants come as cards in her chat.
 */
export function VickiGoals({ accountId, canEdit }: { accountId: string; canEdit: boolean }) {
  const goals = useQuery({ queryKey: qk.vickiGoals(accountId), queryFn: () => api.frontDesk.goals(accountId) });
  const [adding, setAdding] = useState(false);
  const list = goals.data ?? [];
  const active = list.filter((g) => g.status === 'active');
  const done = list.filter((g) => g.status !== 'active');
  return (
    <Card className="p-4">
      <div className="flex items-center gap-2">
        <Target className="size-4 text-primary" />
        <h2 className="flex-1 text-sm font-semibold">{t('VICKI’s goals')}</h2>
        {canEdit && !adding && (
          <Button size="sm" variant="ghost" onClick={() => setAdding(true)}>
            <Plus /> {t('Goal')}
          </Button>
        )}
      </div>
      <p className="mt-1 text-xs text-muted-foreground">
        {canEdit
          ? t('What the business should reach. VICKI works towards it and says where it stands; she only proposes changes, you decide.')
          : t('What the business should reach. Owners and admins set the goals; VICKI works towards them.')}
      </p>
      {adding && <GoalForm accountId={accountId} onDone={() => setAdding(false)} />}
      {!goals.isPending && !active.length && !adding && (
        <p className="mt-3 rounded-lg border border-dashed px-3 py-4 text-center text-xs text-muted-foreground">
          {t('No goals yet. Without goals VICKI only does what she is asked.')}
        </p>
      )}
      <ul className="mt-3 flex flex-col gap-2">
        {active.map((g) => (
          <GoalRow key={g.id} accountId={accountId} goal={g} canEdit={canEdit} />
        ))}
      </ul>
      {done.length > 0 && (
        <details className="mt-3">
          <summary className="cursor-pointer text-xs text-muted-foreground">{t('Reached or dropped ({n})', { n: done.length })}</summary>
          <ul className="mt-2 flex flex-col gap-2 opacity-75">
            {done.map((g) => (
              <GoalRow key={g.id} accountId={accountId} goal={g} canEdit={canEdit} />
            ))}
          </ul>
        </details>
      )}
    </Card>
  );
}

function GoalRow({ accountId, goal: g, canEdit }: { accountId: string; goal: VickiGoal; canEdit: boolean }) {
  const [editing, setEditing] = useState(false);
  const invalidate = [qk.vickiGoals(accountId)];
  const setStatus = useApiMutation((status: VickiGoal['status']) => api.frontDesk.updateGoal(accountId, g.id, { title: g.title, status }), {
    invalidate,
    success: (goal) => (goal.status === 'reached' ? t('Goal reached.') : goal.status === 'dropped' ? t('Goal dropped.') : t('Goal is active again.')),
  });
  const remove = useApiMutation(() => api.frontDesk.deleteGoal(accountId, g.id), { invalidate, success: t('Goal deleted.') });
  if (editing) return <GoalForm accountId={accountId} goal={g} onDone={() => setEditing(false)} />;
  return (
    <li className="rounded-lg border px-3 py-2.5 text-sm">
      {/* On a phone the buttons go under the goal, so its title keeps the width. */}
      <div className="flex flex-col gap-1 sm:flex-row sm:items-start sm:gap-2">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="font-medium">{g.title}</span>
            {g.status !== 'active' && <Badge tone={g.status === 'reached' ? 'green' : 'muted'}>{g.status === 'reached' ? t('Reached') : t('Dropped')}</Badge>}
            {g.dueDate && (
              <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
                <CalendarCheck className="size-3" /> {new Date(`${g.dueDate}T12:00:00`).toLocaleDateString()}
              </span>
            )}
          </div>
          {g.target && <div className="text-xs text-muted-foreground">{t('Target: {target}', { target: g.target })}</div>}
          {g.description && <div className="mt-1 text-xs whitespace-pre-wrap text-muted-foreground">{g.description}</div>}
          {g.progress && (
            <div className="mt-1.5 rounded-md bg-primary/5 px-2 py-1 text-xs">
              <span className="font-medium">VICKI:</span> {g.progress}
              {g.progressAt && (
                <span className="text-muted-foreground">
                  {' '}
                  · <RelativeTime value={g.progressAt} />
                </span>
              )}
            </div>
          )}
        </div>
        {canEdit && (
          <div className="-mr-1 -mb-1 flex shrink-0 gap-0.5 self-end sm:mb-0 sm:self-auto">
            {g.status === 'active' ? (
              <Button size="icon" variant="ghost" onClick={() => setStatus.mutate('reached')} loading={setStatus.isPending} aria-label={t('Mark reached')} title={t('Mark reached')}>
                <Check />
              </Button>
            ) : (
              <Button size="sm" variant="ghost" onClick={() => setStatus.mutate('active')} loading={setStatus.isPending}>
                {t('Activate')}
              </Button>
            )}
            <Button size="icon" variant="ghost" onClick={() => setEditing(true)} aria-label={t('Edit')} title={t('Edit')}>
              <Pencil />
            </Button>
            {g.status === 'active' && (
              <Button size="icon" variant="ghost" onClick={() => setStatus.mutate('dropped')} loading={setStatus.isPending} aria-label={t('Drop goal')} title={t('Drop goal')}>
                <X />
              </Button>
            )}
            <Button
              size="icon"
              variant="ghost"
              onClick={() => window.confirm(t('Delete this goal for good?')) && remove.mutate()}
              loading={remove.isPending}
              aria-label={t('Delete')}
              title={t('Delete')}
            >
              <Trash2 />
            </Button>
          </div>
        )}
      </div>
    </li>
  );
}

function GoalForm({ accountId, goal, onDone }: { accountId: string; goal?: VickiGoal; onDone: () => void }) {
  const [form, setForm] = useState<SaveVickiGoalRequest>({
    title: goal?.title ?? '',
    target: goal?.target ?? '',
    description: goal?.description ?? '',
    dueDate: goal?.dueDate ?? null,
  });
  const save = useApiMutation(() => (goal ? api.frontDesk.updateGoal(accountId, goal.id, form) : api.frontDesk.addGoal(accountId, form)), {
    invalidate: [qk.vickiGoals(accountId)],
    success: goal ? t('Goal saved.') : t('VICKI works towards it from now on.'),
    onSuccess: onDone,
  });
  return (
    <div className="mt-3 flex flex-col gap-2 rounded-lg border bg-muted/30 p-3">
      <Input autoFocus value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder={t('Goal, e.g. More product reviews')} />
      <div className="flex flex-wrap gap-2">
        <Input value={form.target ?? ''} onChange={(e) => setForm({ ...form, target: e.target.value })} placeholder={t('Target, e.g. 50 reviews a month')} className="min-w-48 flex-1" />
        <Input type="date" value={form.dueDate ?? ''} onChange={(e) => setForm({ ...form, dueDate: e.target.value || null })} className="w-40" aria-label={t('Due by')} />
      </div>
      <Textarea value={form.description ?? ''} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} placeholder={t('Why it matters, what VICKI should keep in mind (optional)')} />
      <div className="flex gap-2">
        <Button size="sm" onClick={() => save.mutate()} loading={save.isPending} disabled={!form.title.trim()}>
          {t('Save')}
        </Button>
        <Button size="sm" variant="ghost" onClick={onDone}>
          {t('Cancel')}
        </Button>
      </div>
    </div>
  );
}

/** VICKI's rhythm: her heartbeat and her daily round, in the person's own time. */
export function VickiRhythm({ accountId }: { accountId: string }) {
  const settings = useQuery({ queryKey: qk.frontDeskSettings(accountId), queryFn: () => api.frontDesk.settings(accountId) });
  const save = useApiMutation((patch: Partial<FrontDeskSettings>) => api.frontDesk.setSettings(accountId, patch), {
    invalidate: [qk.frontDeskSettings(accountId)],
    success: t('Saved.'),
  });
  const s = settings.data;
  if (!s) return null;
  return (
    <Card className="flex flex-wrap items-center gap-x-6 gap-y-3 p-4 text-sm">
      <label className="flex items-center gap-2">
        <HeartPulse className="size-4 text-primary" />
        <span className="font-medium">{t('Heartbeat')}</span>
        <Select value={s.heartbeatMinutes ? String(s.heartbeatMinutes) : 'off'} onValueChange={(v) => save.mutate({ heartbeatMinutes: v === 'off' ? null : Number(v) })}>
          <SelectTrigger size="sm" className="w-44">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {BEATS.map((b) => (
              <SelectItem key={b.value || 'off'} value={b.value || 'off'}>
                {b.label}
              </SelectItem>
            ))}
            {s.heartbeatMinutes && !BEATS.some((b) => b.value === String(s.heartbeatMinutes)) && (
              <SelectItem value={String(s.heartbeatMinutes)}>{t('every {n} minutes', { n: s.heartbeatMinutes })}</SelectItem>
            )}
          </SelectContent>
        </Select>
      </label>
      <label className="flex items-center gap-2">
        <CalendarCheck className="size-4 text-primary" />
        <span className="font-medium">{t('Daily round')}</span>
        <Switch checked={s.rounds} onCheckedChange={(on) => save.mutate({ rounds: on })} aria-label={t('Daily round')} />
        <Select value={String(toLocal(s.roundHour))} onValueChange={(h) => save.mutate({ roundHour: toUtc(Number(h)) })} disabled={!s.rounds}>
          <SelectTrigger size="sm" className="w-24">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {Array.from({ length: 24 }, (_, h) => (
              <SelectItem key={h} value={String(h)}>
                {`${String(h).padStart(2, '0')}:00`}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </label>
      <p className="basis-full text-xs text-muted-foreground">
        {t('At each heartbeat VICKI looks at what is new and at her goals; in her daily round she reports, looks after what hangs and learns. Each one is a run of her model.')}
      </p>
    </Card>
  );
}
